import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { isAuthenticated } from '../middleware/authentication';
import { fetchBetaFeatures as fetchBetaFeaturesApi } from '../services/user';
import { clearSentryUser, setSentryUser } from '@odo/shared-ui/observability/sentry';
import { AUTH_TOKEN_STORAGE_KEY } from '../api/config';
import { clearPersistedQueryCache } from './queryClient';

interface User {
    id: string;
    email: string;
    name: string;
    unread_count: number;
    betaFeatures?: string[];
    profilePictureUrl?: string | null;
    database: {
        data: {
            isAdmin: boolean;
            practices: string[];
            lastSeenRelease?: string | null;
            lastSeenReleases?: Record<string, string>;
            id: string;
        };
    };
}

interface UserContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (userData: User) => void;
    logout: () => void;
    setUnreadCount: (count: number) => void;
    getUnreadCount: () => number;
    updateUser: (userData: Partial<User>) => void;
    fetchBetaFeatures: () => Promise<string[]>;
    setProfilePictureUrl: (url: string) => void;
}

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider Props
interface UserProviderProps {
    children: ReactNode;
}

// Provider Component
function UserProvider({ children }: UserProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    // Check authentication on mount
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const userData = await isAuthenticated();
                if (userData) {
                    setSentryUser(userData);
                    setUser(userData);
                }
            } catch {
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, []);

    // /user/verify omits betaFeatures; fetch once and merge. Guarded by
    // "already have it" plus an in-flight ref so it fires once per session.
    const betaFeaturesFetchInFlight = useRef(false);
    useEffect(() => {
        if (!user || user.betaFeatures !== undefined || betaFeaturesFetchInFlight.current) {
            return;
        }
        betaFeaturesFetchInFlight.current = true;
        fetchBetaFeaturesApi()
            .then((betaFeatures) => {
                setUser((prev) => (prev ? { ...prev, betaFeatures } : prev));
            })
            .finally(() => {
                betaFeaturesFetchInFlight.current = false;
            });
    }, [user]);

    // Login function
    const login = useCallback((userData: User) => {
        setSentryUser(userData);
        setUser(userData);
    }, []);

    // Logout function
    const logout = useCallback(() => {
        clearSentryUser();
        setUser(null);
        localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
        // Identity-free query keys: this user's rows must not outlive them.
        clearPersistedQueryCache();
    }, []);

    // Update user function
    const updateUser = useCallback(
        (userData: Partial<User>) => {
            if (user) {
                setUser({ ...user, ...userData });
            }
        },
        [user]
    );

    const setUnreadCount = useCallback(
        (count: number) => {
            if (user) {
                setUser({ ...user, unread_count: count });
            }
        },
        [user]
    );

    const getUnreadCount = useCallback(() => {
        return user?.unread_count || 0;
    }, [user]);

    const setProfilePictureUrl = useCallback(
        (url: string) => {
            if (user) {
                setUser({ ...user, profilePictureUrl: url });
            }
        },
        [user]
    );

    const value: UserContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        updateUser,
        setUnreadCount,
        getUnreadCount,
        fetchBetaFeatures: fetchBetaFeaturesApi,
        setProfilePictureUrl
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export { UserProvider };

// Custom hook to use the user context
function useUser(): UserContextType {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}

export { useUser };
